import React, { useCallback, useEffect, useState } from 'react';
import config from '../../config/config';
import { authHeaders } from '../../utils/session';
import { inactiveBanner, collectionLine, replacesLine, eventRow, REACTIVATE_REASON_MIN } from './assetLifecycle';

// LC-4b — the device's lifecycle on its Asset page (asset-lifecycle design §8):
//   the old device   → a banner «לא פעיל · שודרג ל־…», the condition at collection, and
//                      «החזר למצב פעיל» for a service manager / admin while not final (UQ2);
//   the new device   → «מחליף את <old> (לא פעיל)», linked;
//   both             → «מחזור חיים של המכשיר (N)» — every event, oldest first.
// A device with no lifecycle record renders nothing.
const AssetLifecyclePanel = ({ assetId, onReactivated }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    if (!assetId) { setData(null); return undefined; }
    let cancelled = false;
    fetch(`${config.api.baseUrl}/api/assets/${encodeURIComponent(assetId)}/lifecycle`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [assetId]);

  useEffect(() => load(), [load]);

  if (!data || !(data.events || []).length) return null;

  const banner = inactiveBanner(data);
  const collection = collectionLine(data);
  const replaces = replacesLine(data.events);

  const reactivate = async () => {
    const reason = window.prompt('סיבת ההחזרה למצב פעיל (חובה):');
    if (reason === null) return;
    if (String(reason).trim().length < REACTIVATE_REASON_MIN) {
      setMsg({ ok: false, text: `יש לפרט סיבה (לפחות ${REACTIVATE_REASON_MIN} תווים)` });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/assets/${encodeURIComponent(assetId)}/lifecycle/reactivate`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: String(reason).trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        const det = d && d.detail;
        setMsg({ ok: false, text: (det && (det.message || (typeof det === 'string' ? det : ''))) || 'ההחזרה נכשלה' });
      } else {
        setMsg({ ok: true, text: 'הנכס הוחזר למצב פעיל — רענן את הדף כדי לראות את הסטטוס המעודכן' });
        load();
        if (onReactivated) onReactivated();
      }
    } catch (e) {
      setMsg({ ok: false, text: 'ההחזרה נכשלה' });
    }
    setBusy(false);
  };

  return (
    <div dir="rtl" data-testid="asset-lifecycle" style={{ marginBottom: 12 }}>
      {banner && (
        <div data-testid="asset-inactive-banner" style={{ background: '#475569', color: '#fff', padding: '10px 16px',
          borderRadius: 8, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>{banner}</span>
          {data.can_reactivate && (
            <button type="button" onClick={reactivate} disabled={busy} data-testid="asset-reactivate"
              style={{ background: '#fff', color: '#334155', border: 'none', borderRadius: 6, padding: '6px 12px',
                       fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'מחזיר…' : 'החזר למצב פעיל'}
            </button>
          )}
        </div>
      )}
      {collection && (
        <div data-testid="asset-collection-line" style={{ background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 8, padding: '8px 16px', marginTop: 6, fontSize: 13, color: '#334155' }}>
          {collection}
        </div>
      )}
      {replaces && (
        <div data-testid="asset-replaces" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
          padding: '8px 16px', marginTop: 6, fontSize: 13, color: '#1e3a8a' }}>
          <a href={`/object/Asset/${replaces.assetId}`} style={{ color: '#1d4ed8', fontWeight: 700 }}>{replaces.text}</a>
        </div>
      )}
      {msg && (
        <div role="status" style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, fontSize: 13,
          background: msg.ok ? '#dcfce7' : '#fee2e2', color: msg.ok ? '#166534' : '#991b1b' }}>{msg.text}</div>
      )}
      <div style={{ marginTop: 6 }}>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="asset-lifecycle-toggle"
          style={{ background: 'none', border: 'none', color: '#0d7377', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
          {open ? '▾' : '▸'} מחזור חיים של המכשיר ({data.events.length})
        </button>
        {open && (
          <div data-testid="asset-lifecycle-events" style={{ marginTop: 6, borderRight: '3px solid #cbd5e1', paddingRight: 10 }}>
            {data.events.map((e) => {
              const r = eventRow(e);
              return (
                <div key={e.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                  <div><b>{r.title}</b>{r.date ? ` · ${r.date}` : ''}{r.actor ? ` · ${r.actor}` : ''}</div>
                  {r.detail && <div style={{ color: '#475569' }}>{r.detail}</div>}
                  {r.text && <div style={{ color: '#64748b' }}>"{r.text}"</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetLifecyclePanel;
